// app/src/lib/procurement/vendorMatch.js — fuzzy vendor-name identity.
//
// The OCR'd vendor name varies slightly between receipts from the same supplier
// ("Lam's Seafood" one week, "Lam's Seafood Market" the next; "US Chef's Store"
// vs "US FOODS | CHEF'S STORE"). Exact normalized-name equality treats those as
// different vendors and creates duplicates. These helpers decide when two names
// plausibly refer to the SAME vendor so the OCR save path can reuse the existing
// row (and the vendor screen can offer to merge dupes already on file).
//
// Precision matters: a wrong merge silently mixes two suppliers' spend and price
// history. The rules below are tuned to catch real OCR variants while NOT merging
// two distinct vendors that merely share a category word ("Lam's Seafood" vs
// "King's Seafood"). See test/vendorMatch.test.js for the full contract.

import { normalize, similarity } from './util.js';

// Filler words that carry no identity signal; dropped before token comparison.
const GENERIC = new Set(['the', 'co', 'inc', 'llc', 'ltd', 'corp', 'and', 'of', 'dba']);

// Containment needs a reasonably long shared string so "us" doesn't match
// everything; a distinctive shared token must be at least this long.
const MIN_CONTAINMENT_LEN = 6;
const MIN_DISTINCTIVE_LEN = 4;
// A single leading word this short or longer can stand in for a whole vendor
// (the "QFC" of "QFC Quality Food Centers"). Set above 2 so "US" stays distinct.
const MIN_ACRONYM_LEN = 3;
const MIN_JACCARD = 0.5;
const MIN_SIMILARITY = 0.84;

/** Split a name into lowercase word tokens, dropping generic filler words. */
export function tokenize(name) {
  return String(name || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w && !GENERIC.has(w));
}

// Tokens that actually carry identity signal: drop 1-char fragments like the
// "s" left behind by splitting "Chef's" / "Lam's", which would otherwise inflate
// the overlap between any two possessive names.
function significant(tokens) {
  return tokens.filter((t) => t.length >= 2);
}

function jaccard(aTokens, bTokens) {
  const a = new Set(aTokens), b = new Set(bTokens);
  if (!a.size || !b.size) return 0;
  let inter = 0;
  for (const x of a) if (b.has(x)) inter++;
  return inter / (a.size + b.size - inter);
}

/**
 * How strongly do names `a` and `b` match? Symmetric.
 *  - 'exact'  normalized strings are identical.
 *  - 'strong' one name contains the other (a word added/dropped) OR they share a
 *             distinctive token with subset / high token overlap. Structurally
 *             tied, so safe to LEARN as a permanent alias.
 *  - 'weak'   only whole-string edit-distance similarity matched (OCR typo /
 *             abbreviation). Plausible enough to reuse the vendor, but too
 *             error-prone to persist as an alias.
 *  - null     not a match.
 * @returns {'exact'|'strong'|'weak'|null}
 */
export function matchStrength(a, b) {
  const an = normalize(a), bn = normalize(b);
  if (!an || !bn) return null;
  if (an === bn) return 'exact';

  const at = significant(tokenize(a));
  const bt = significant(tokenize(b));

  // 1. Containment — one name is the other plus extra words
  //    ("Lam's Seafood" ⊂ "Lam's Seafood Market", "Costco" ⊂ "Costco Wholesale").
  const shorter = an.length <= bn.length ? an : bn;
  const longer = an.length <= bn.length ? bn : an;
  if (shorter.length >= MIN_CONTAINMENT_LEN && longer.includes(shorter)) return 'strong';

  // 1b. Brand prefix / acronym — one name is a SINGLE distinctive word that is
  //     the other's LEADING word: "QFC" ⊂ "QFC Quality Food Centers", "Sysco" ⊂
  //     "Sysco Seattle". This is the case containment misses when the brand is
  //     short (an acronym below MIN_CONTAINMENT_LEN). Two guards keep precision:
  //       - anchored at the FIRST token, so a mid-name coincidence ("Foods" in
  //         "Sea Foods") can't trigger it; and
  //       - the word must be ≥3 chars, so a 2-letter prefix like "US" still
  //         won't swallow "US Foods" (a distinct vendor on these books).
  const leads = (one, many) =>
    one.length === 1 && many.length >= 1 && one[0].length >= MIN_ACRONYM_LEN && one[0] === many[0];
  if (leads(at, bt) || leads(bt, at)) return 'strong';

  // 2. Token overlap — share enough significant words AND at least one
  //    distinctive (non-generic, ≥4 char) word, so two suppliers in the same
  //    category don't merge on the category word alone.
  const shared = at.filter((t) => bt.includes(t));
  const hasDistinctiveShared = shared.some((t) => t.length >= MIN_DISTINCTIVE_LEN);
  if (hasDistinctiveShared) {
    const small = at.length <= bt.length ? at : bt;
    const bigSet = new Set(at.length <= bt.length ? bt : at);
    const isSubset = small.length > 0 && small.every((t) => bigSet.has(t));
    if (isSubset || jaccard(at, bt) >= MIN_JACCARD) return 'strong';
  }

  // 3. Whole-string edit-distance similarity — catches OCR typos / abbreviations
  //    ("Restaurant Depot" vs "Restaurant Dep0t").
  if (similarity(a, b) >= MIN_SIMILARITY) return 'weak';

  return null;
}

/**
 * Do names `a` and `b` plausibly refer to the same vendor? Symmetric.
 * @returns {boolean}
 */
export function namesMatch(a, b) {
  return matchStrength(a, b) !== null;
}

/**
 * Best matching vendor for an OCR'd name, or null. Matches against each vendor's
 * name AND its recorded aliases. `excludeId` skips a vendor (e.g. itself).
 * @param {string} name
 * @param {Array<{ vendorId:string, name:string, aliases?:string[] }>} vendors
 * @param {string=} excludeId
 */
export function bestVendorMatch(name, vendors, excludeId) {
  if (!normalize(name) || !Array.isArray(vendors)) return null;
  const nameTokens = significant(tokenize(name));
  let best = null, bestScore = -1;
  for (const v of vendors) {
    if (!v || v.vendorId === excludeId) continue;
    const candidates = [v.name, ...(v.aliases || [])].filter(Boolean);
    if (!candidates.some((c) => namesMatch(name, c))) continue;
    // Prefer the closest by token overlap; exact-normalized name wins outright.
    const score = jaccard(nameTokens, significant(tokenize(v.name)))
      + (normalize(v.name) === normalize(name) ? 1 : 0);
    if (score > bestScore) { bestScore = score; best = v; }
  }
  return best;
}
